import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { api } from '../lib/api'

export interface SectionPerm {
  visible: boolean
  read_only: boolean
}

export interface MemberAccess {
  is_owner: boolean
  role: string | null
  sections_access: Record<string, SectionPerm>
}

const _OWNER_ACCESS: MemberAccess = {
  is_owner: true,
  role: null,
  sections_access: {
    executive:    { visible: true, read_only: false },
    revenue_ops:  { visible: true, read_only: false },
    margin:       { visible: true, read_only: false },
    billing:      { visible: true, read_only: false },
    recurring:    { visible: true, read_only: false },
    cost_entries: { visible: true, read_only: false },
  },
}

interface MemberContextValue {
  access: MemberAccess
  loading: boolean
  canAccess: (section: string) => boolean
  isReadOnly: (section: string) => boolean
  firstAllowedPath: () => string
}

const MemberContext = createContext<MemberContextValue>({
  access: _OWNER_ACCESS,
  loading: false,
  canAccess: () => true,
  isReadOnly: () => false,
  firstAllowedPath: () => '/',
})

const _SECTION_PATHS: [string, string][] = [
  ['executive', '/'],
  ['revenue_ops', '/revenue'],
  ['margin', '/margin'],
  ['billing', '/billing'],
  ['recurring', '/recurring-revenue'],
  ['cost_entries', '/costi'],
]

export function MemberProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [access, setAccess] = useState<MemberAccess>(_OWNER_ACCESS)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (authLoading || !user) {
      setAccess(_OWNER_ACCESS)
      return
    }
    setLoading(true)
    api.get<MemberAccess>('/api/v1/members/me')
      .then(data => setAccess(data))
      .catch(() => setAccess(_OWNER_ACCESS)) // fail-open
      .finally(() => setLoading(false))
  }, [user?.id, authLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  function canAccess(section: string): boolean {
    return access.sections_access[section]?.visible ?? false
  }

  function isReadOnly(section: string): boolean {
    return access.sections_access[section]?.read_only ?? false
  }

  function firstAllowedPath(): string {
    for (const [section, path] of _SECTION_PATHS) {
      if (access.sections_access[section]?.visible) return path
    }
    return '/impostazioni'
  }

  return (
    <MemberContext.Provider value={{ access, loading, canAccess, isReadOnly, firstAllowedPath }}>
      {children}
    </MemberContext.Provider>
  )
}

export function useMember() {
  return useContext(MemberContext)
}
